import React from "react";
import type { DescriptionsProps as AntdDescriptionsProps } from "antd";
import { createStyles, CreateStylesUtils } from "antd-style";

// Styles
const styleUtils = createStyles(({ token }: CreateStylesUtils) => {
  const baseItem = {
    display: "grid",
    gridTemplateColumns: "minmax(180px, 20%) 1fr",
    borderRadius: token.borderRadiusSM,
    transition: `background-color ${token.motionDurationMid}`,
    position: "relative" as const,
    "&:hover": {
      backgroundColor: token.colorFillQuaternary,
    },
    "&[data-state=selected]": {
      backgroundColor: "rgba(0, 0, 0, 0.05)",
    },
    "@media (max-width: 767px)": {
      gridTemplateColumns: "1fr",
      "& + &": { marginTop: 14 },
    },
    "@media (min-width: 992px)": {
      gridTemplateColumns: "minmax(240px, 20%) 1fr",
    },
  };

  return {
    container: {
      position: "relative",
      fontSize: token.fontSize,
      color: token.colorTextSecondary,
      marginLeft: -8,
      marginRight: -8,
    },
    item: baseItem,
    itemBordered: {
      ...baseItem,
      "&:not(:last-child)::after": {
        content: '""',
        position: "absolute",
        bottom: 0,
        left: 8,
        right: 8,
        height: 1,
        backgroundColor: token.colorFillTertiary,
      },
    },
    label: {
      padding: "8px",
      color: "#73767b",
      fontWeight: "500",
      verticalAlign: "top",
      "&:has([role=checkbox])": { paddingRight: 0 },
      "@media (max-width: 767px)": { padding: "4px 8px" },
    },
    content: {
      padding: "8px",
      color: token.colorText,
      verticalAlign: "middle",
      fontWeight: "500",
      "&:has([role=checkbox])": { paddingRight: 0 },
      "@media (max-width: 767px)": { padding: "2px 8px" },
    },
  };
});

interface DescriptionsItemProps extends React.HTMLAttributes<HTMLDivElement> {
  label?: React.ReactNode;
  bordered?: boolean;
}

// Components
const DescriptionsLabel = ({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
  const { styles, cx } = styleUtils();
  return (
    <div className={cx(styles.label, className)} {...props}>
      {children}
    </div>
  );
};

const DescriptionsContent = ({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
  const { styles, cx } = styleUtils();
  return (
    <div className={cx(styles.content, className)} {...props}>
      {children}
    </div>
  );
};

const DescriptionsItem = ({
  className,
  label,
  children,
  bordered,
  ...props
}: DescriptionsItemProps) => {
  const { styles, cx } = styleUtils();
  const itemClass = bordered ? styles.itemBordered : styles.item;

  return (
    <div className={cx(itemClass, className)} {...props}>
      {label !== undefined && label !== null ? (
        <>
          <DescriptionsLabel>{label}</DescriptionsLabel>
          <DescriptionsContent>{children}</DescriptionsContent>
        </>
      ) : (
        children
      )}
    </div>
  );
};

interface DescriptionsProps {
  className?: string;
  items: NonNullable<AntdDescriptionsProps["items"]>;
  bordered?: boolean;
}

const Descriptions: React.FC<DescriptionsProps> = ({
  className,
  items,
  bordered = false,
}) => {
  const { styles, cx } = styleUtils();

  return (
    <div className={cx(styles.container, className)}>
      {items.map((item, index) =>
        item.children != null && item.children !== "" ? (
          <DescriptionsItem
            key={item.key ?? index}
            label={item.label}
            bordered={bordered}
          >
            {item.children}
          </DescriptionsItem>
        ) : null,
      )}
    </div>
  );
};

export { Descriptions };
